import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import cn from 'classnames';
import { Col, Row, Form, Radio } from 'antd';

import './styles.scss';
import {
  DOMAIN_PATTERN,
  PHONE_PATTERN,
  POLITICAL_TYPES_NAME,
} from 'constants/common';
import { useSavePoliticalDeclaration } from '../../services';
import useProfile from 'hooks/useProfile';
import { showModalSuccessfulScreen } from 'utilities/modal';
import { InputHttp, MyButton, MyForm, MyInput, MyModal } from 'components/Atoms';
import { InformationPopover } from 'components/Molecules';
import { PHONE_ERROR_MESSAGE } from 'constants/text';
import {
  useGetAdvertiser,
  useUpdateAdvertiser,
} from 'components/Pages/Library/components/Advertisers/services';
import { FormattedMessage, useIntl } from 'react-intl';
import { t } from 'utilities/lang';

const ModalPoliticalAdvertiser = ({
  onCloseModalInCampaign,
  isOpened,
  setIsOpened,
  isEdit,
}) => {
  const intl = useIntl();

  const formRules = {
    displayName: [
      {
        required: true,
        message: intl.formatMessage({ id: 'modal_political_advertiser.enter_official_campaign_committee_name' })
      },
    ],
    authorizedAgent: [
      { required: true, message: intl.formatMessage({ id: 'modal_political_advertiser.enter_authorized_agent' }) },
    ],
    website: [
      { required: true, message: intl.formatMessage({ id: 'modal_political_advertiser.enter_website' }) },
      {
        pattern: DOMAIN_PATTERN,
        message: intl.formatMessage({ id: 'modal_political_advertiser.invalid_campaign_website_url' }),
      },
    ],
    phone: [
      { required: true, message: intl.formatMessage({ id: 'modal_political_advertiser.enter_phone_number' }) },
      {
        pattern: PHONE_PATTERN,
        message: PHONE_ERROR_MESSAGE,
      },
    ],
    street: [{ required: true, message: intl.formatMessage({ id: 'modal_political_advertiser.enter_street' }) }],
    city: [{ required: true, message: intl.formatMessage({ id: 'modal_political_advertiser.enter_city' }) }],
    state: [{ required: true, message: intl.formatMessage({ id: 'modal_political_advertiser.enter_state' }) }],
    zipCode: [{ required: true, message: intl.formatMessage({ id: 'modal_political_advertiser.enter_zip_code' }) }],
    financialDisclosureUrl: [
      { required: true, message: intl.formatMessage({ id: 'modal_political_advertiser.enter_financial_disclosure_url' }) },
      {
        pattern: DOMAIN_PATTERN,
        message: intl.formatMessage({ id: 'modal_political_advertiser.invalid_financial_disclosure_url' }),
      },
    ],
    otherStateSpecificInformation: [{ required: false }],
    paidForByDisclaimer: [
      { required: true, message: intl.formatMessage({ id: 'modal_political_advertiser.enter_disclaimer' }) },
    ],
    politicalType: [
      {
        required: true,
        message: intl.formatMessage({ id: 'modal_political_advertiser.select_political_type' }),
      },
    ]
  };

const formItems = [
  {
    name: 'displayName',
    label: <FormattedMessage id="modal_political_advertiser.official_campaign_committee_name" />,
    fieldName: intl.formatMessage({ id: 'modal_political_advertiser.official_campaign_committee_name' }),
    layout: { xs: 24, md: 12 },
    rules: formRules.displayName,
  },
  {
    name: 'authorizedAgent',
    label: <FormattedMessage id="modal_political_advertiser.authorized_agent" />,
    fieldName: intl.formatMessage({ id: 'modal_political_advertiser.authorized_agent' }),
    layout: { xs: 24, md: 12 },
    rules: formRules.authorizedAgent,
  },
  {
    name: 'website',
    label: <FormattedMessage id="modal_political_advertiser.campaign_website_url" />,
    fieldName: intl.formatMessage({ id: 'modal_political_advertiser.campaign_website_url' }),
    layout: { xs: 24, md: 12 },
    rules: formRules.website,
  },
  {
    name: 'street',
    label: <FormattedMessage id="modal_political_advertiser.street_address" />,
    fieldName: intl.formatMessage({ id: 'modal_political_advertiser.street_address' }),
    layout: { xs: 24, md: 12 },
    rules: formRules.street,
  },
  {
    name: 'city',
    label: <FormattedMessage id="modal_political_advertiser.city" />,
    fieldName: intl.formatMessage({ id: 'modal_political_advertiser.city' }),
    layout: { xs: 24, sm: 12, md: 6 },
    rules: formRules.city,
  },
  {
    name: 'state',
    label: <FormattedMessage id="modal_political_advertiser.state" />,
    fieldName: intl.formatMessage({ id: 'modal_political_advertiser.state' }),
    layout: { xs: 24, sm: 12, md: 6 },
    rules: formRules.state,
  },
  {
    name: 'zip',
    label: <FormattedMessage id="modal_political_advertiser.zip" />,
    fieldName: intl.formatMessage({ id: 'modal_political_advertiser.zip' }),
    layout: { xs: 24, sm: 12, md: 6 },
    rules: formRules.zipCode,
  },
  {
    name: 'phone',
    label: <FormattedMessage id="modal_political_advertiser.phone" />,
    fieldName: intl.formatMessage({ id: 'modal_political_advertiser.phone' }),
    layout: { xs: 24, sm: 12, md: 6 },
    rules: formRules.phone,
  },
  {
    name: 'paidForByDisclaimer',
    label: <FormattedMessage id="modal_political_advertiser.paid_for_by_disclaimer" />,
    fieldName: intl.formatMessage({ id: 'modal_political_advertiser.paid_for_by_disclaimer' }),
    layout: { xs: 24, sm: 12 },
    rules: formRules.paidForByDisclaimer,
  },
  {
    name: 'otherStateSpecificInformation',
    label: <FormattedMessage id="modal_political_advertiser.other_state_specific_information" />,
    fieldName: intl.formatMessage({ id: 'modal_political_advertiser.other_state_specific_information' }),
    layout: { xs: 24, sm: 12 },
    rules: formRules.otherStateSpecificInformation,
  },
];

  const politicalTypes = ['Federal', 'StateLocalRace'];
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const { data: userInfo } = useProfile();

  //SERVICE
  const savePoliticalDeclaration = useSavePoliticalDeclaration();
  const { data: advertiserData } = useGetAdvertiser(userInfo?.advertiserId, {
    enabled: !!userInfo?.isSavePoliticalDeclaration && !!userInfo?.advertiserId,
  });
  const { mutate: updateAdvertiser } = useUpdateAdvertiser();

  useEffect(() => {
    if (advertiserData) {
      const {
        name,
        domainUrl,
        isPolitical,
        politicalType,
        displayName,
        authorizedAgent,
        website,
        phone,
        street,
        city,
        state,
        zip,
        financialDisclosureUrl,
        paidForByDisclaimer,
        otherStateSpecificInformation,
      } = advertiserData;

      form.setFieldsValue({
        name,
        domainUrl,
        isPolitical: !!isPolitical,
        politicalType,
        displayName,
        authorizedAgent,
        website,
        phone,
        street,
        city,
        state,
        zip,
        financialDisclosureUrl,
        paidForByDisclaimer,
        otherStateSpecificInformation,
      });
    }

    if (!Boolean(userInfo?.advertiserId)) {
      form.setFieldsValue({
        name: '',
        domainUrl: '',
        isPolitical: '',
        politicalType: '',
        displayName: '',
        authorizedAgent: '',
        website: '',
        phone: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        financialDisclosureUrl: '',
        paidForByDisclaimer: '',
        otherStateSpecificInformation: '',
      });
    }
  }, [userInfo?.advertiserId, advertiserData, form]);

  const onFinish = (values) => {
    setLoading(true);

    isEdit
      ? updateAdvertiser(
          {
            ...values,
            isPolitical: true,
            id: advertiserData.id,
          },
          {
            onSuccess: () => {
              setLoading(false);
              setIsOpened(false);

              showModalSuccessfulScreen({
                title: t('modal_political_advertiser.success_title'),
                description: t('modal_political_advertiser.success_update_description'),
                primaryButton: t('common.ok'),
              });
            },
            onError: () => {
              setLoading(false);
            },
          }
        )
      : savePoliticalDeclaration.mutate(
          {
            ...values,
          },
          {
            onSuccess: () => {
              setLoading(false);
              setIsOpened(false);

              showModalSuccessfulScreen({
                title: t('modal_political_advertiser.success_title'),
                description: t('modal_political_advertiser.success_save_description'),
                primaryButton: t('common.ok'),
              });
            },
            onError: () => {
              setLoading(false);
            },
          }
        );
  };

  const onCloseModal = useCallback(() => {
    setIsOpened(false);
  }, [setIsOpened]);

  return (
    <MyModal
      className="modal-political-advertiser"
      visible={isOpened}
      width={768}
      title={
        isEdit
          ? <FormattedMessage id="modal_political_advertiser.edit_political_disclaimer" />
          : <FormattedMessage id="modal_political_advertiser.required_political_advertising_disclosure" />
      }
      footer={
        <Row className="flex-center">
          <Col span={6}>
            <MyButton
              loading={loading}
              type="primary"
              className="btn-primary-custom w-100p h-45"
              onClick={form.submit}
            >
              <FormattedMessage id="common.submit" />
            </MyButton>
          </Col>
        </Row>
      }
      onClose={onCloseModalInCampaign || onCloseModal}
    >
      <MyForm form={form} onFinish={onFinish} requiredMark="">
        <Row justify="space-between">
          <Col xs={{ span: 24, order: 2 }} md={{ span: 12, order: 1 }}>
            <div className="mt-30">
              <Form.Item
                name="politicalType"
                label={<FormattedMessage id="modal_political_advertiser.political_type_question" />}
                initialValue={
                  advertiserData?.politicalType ?? politicalTypes[1]
                }
                rules={formRules.politicalType}
              >
                <Radio.Group>
                  <Row gutter={[35]} wrap={false}>
                    {politicalTypes.map((type) => (
                      <Col key={type}>
                        <Radio value={type}>{POLITICAL_TYPES_NAME[type]}</Radio>
                      </Col>
                    ))}
                  </Row>
                </Radio.Group>
              </Form.Item>
            </div>
          </Col>
        </Row>
        <div className="mt-40">
          <Row gutter={[20, 20]}>
            {formItems.map(({ name, label, fieldName, layout, rules }) => {
              return (
                <>
                  <Col key={name} {...layout}>
                    <div
                      className={cn({
                        'input-with-information-popover': name === 'phone',
                      })}
                    >
                      <Form.Item name={name} label={label} rules={rules}>
                        { name === 'website' ? (
                          <InputHttp placeholder={intl.formatMessage({ id: 'modal_political_advertiser.enter_field'}, { field: fieldName } )} />
                        ) : (
                          <MyInput placeholder={intl.formatMessage({ id: 'modal_political_advertiser.enter_field'}, { field: fieldName } )} />
                        )}
                      </Form.Item>
                      {name === 'phone' && (
                        <InformationPopover
                          placement="right"
                          childClassName="svg-vertical-inherit"
                          content={
                            <div className="t-desc political-content">
                              <FormattedMessage id="modal_political_advertiser.phone_tooltip_text" />
                            </div>
                          }
                        />
                      )}
                    </div>
                  </Col>
                </>
              );
            })}
            <Col xs={24} md={12}>
              <Form.Item
                shouldUpdate={(prev, cur) =>
                  prev.politicalType !== cur.politicalType
                }
              >
                {() => {
                  const politicalType = form.getFieldValue('politicalType');
                  if (politicalType !== politicalTypes[0]) return null;
                  return (
                    <Form.Item
                      name="financialDisclosureUrl"
                      label={<FormattedMessage id="modal_political_advertiser.financial_disclosure_url" />}
                      rules={formRules.financialDisclosureUrl}
                    >
                      <InputHttp placeholder={intl.formatMessage({ id: 'modal_political_advertiser.enter_field' }, { field: intl.formatMessage({ id: 'modal_political_advertiser.financial_disclosure_url' }) } )} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </div>
      </MyForm>
    </MyModal>
  );
};
export default ModalPoliticalAdvertiser;
